import React, { useRef, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import skipForward10 from "../../../../../assets/images/report/skipBack10.svg";
import skipBackward10 from "../../../../../assets/images/report/skipForward10.svg";
import { AudioPlayingContext } from "../../../../../contexts/AudioPlayingContext";

interface RecordingProps {
  url?: string;
  questionId?: string;
  initialDuration?: number;
}

const Recording: React.FC<RecordingProps> = ({
  url,
  questionId = "audio",
  initialDuration,
}) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(initialDuration);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isAudioLoaded, setIsAudioLoaded] = React.useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Get audio context
  const audioContext = React.useContext(AudioPlayingContext);

  if (!audioContext) {
    throw new Error("Recording must be used within an AudioPlayingProvider");
  }

  const { controlAudio, stopControlledAudio, currentControlledId } =
    audioContext;

  const audioId = `${questionId}-${url}`;

  // Set initial duration when component mounts or when initialDuration changes
  useEffect(() => {
    if (initialDuration && initialDuration > 0) {
      setDuration(initialDuration);
    }
  }, [initialDuration]);

  // Update playing state based on context
  useEffect(() => {
    setIsPlaying(currentControlledId === audioId);
  }, [currentControlledId, audioId]);

  const formatTime = (time: number) => {
    if (!time || isNaN(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Reset when question or URL changes
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);

    if (!url) {
      setDuration(initialDuration || 0);
      setIsAudioLoaded(false);
    }

    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.pause();
    }
  }, [questionId, url, initialDuration]);

  useEffect(() => {
    if (audioRef.current) {
      const audio = audioRef.current;

      const handleTimeUpdate = () => {
        if (!isDragging && !isNaN(audio.currentTime)) {
          setCurrentTime(audio.currentTime);
        }
      };

      const handleLoadedMetadata = () => {
        // Only update duration if we get a finite duration that's greater than our initialDuration
        if (
          Number.isFinite(audio.duration) &&
          audio.duration > 0 &&
          (!initialDuration || audio.duration > initialDuration)
        ) {
          setDuration(audio.duration);
        }
        setIsAudioLoaded(true);
      };

      const handleDurationChange = () => {
        if (
          Number.isFinite(audio.duration) &&
          audio.duration > 0 &&
          (!initialDuration || audio.duration > initialDuration)
        ) {
          setDuration(audio.duration);
        }
      };

      const handleEnded = () => {
        stopControlledAudio();
        setCurrentTime(0);
        audio.currentTime = 0;
      };

      const handleError = (e: ErrorEvent) => {
        console.error("Audio error:", e);
        setIsAudioLoaded(false);
        setDuration(initialDuration || 0);
        stopControlledAudio();
      };

      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("durationchange", handleDurationChange);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("error", handleError);

      // Try to get duration immediately if already loaded
      if (audio.readyState >= 1) {
        if (
          Number.isFinite(audio.duration) &&
          audio.duration > 0 &&
          (!initialDuration || audio.duration > initialDuration)
        ) {
          setDuration(audio.duration);
        }
        setIsAudioLoaded(true);
      }

      return () => {
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("durationchange", handleDurationChange);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("error", handleError);
      };
    }
  }, [isDragging, url, initialDuration, stopControlledAudio]);

  const handlePlayPause = () => {
    if (!url || !isAudioLoaded || !audioRef.current) return;

    if (isPlaying) {
      stopControlledAudio();
    } else {
      controlAudio(audioId, audioRef.current);
    }
  };

  const handleProgressClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (!url || !isAudioLoaded || !duration) return;
    if (progressBarRef.current && audioRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const clickPosition = (e.clientX - rect.left) / rect.width;
      const newTime = clickPosition * duration;

      if (!isNaN(newTime) && newTime >= 0 && newTime <= duration) {
        setCurrentTime(newTime);
        audioRef.current.currentTime = newTime;
      }
    }
  };

  const handleProgressDragStart = () => {
    if (!url || !isAudioLoaded || !duration) return;
    setIsDragging(true);
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
    }
  };

  const handleProgressDragEnd = () => {
    if (!url || !isAudioLoaded || !duration) return;
    setIsDragging(false);
    if (isPlaying && audioRef.current) {
      audioRef.current.play().catch(console.error);
    }
  };

  const handleProgressDrag = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (!url || !isAudioLoaded || !duration) return;
    if (isDragging && progressBarRef.current && audioRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const clickPosition = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width)
      );
      const newTime = clickPosition * duration;

      if (!isNaN(newTime) && newTime >= 0 && newTime <= duration) {
        setCurrentTime(newTime);
        audioRef.current.currentTime = newTime;
      }
    }
  };

  const skipForward = () => {
    if (!url || !isAudioLoaded || !duration) return;
    if (audioRef.current) {
      const newTime = Math.min(duration, currentTime + 10);
      if (!isNaN(newTime)) {
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
      }
    }
  };

  const skipBackward = () => {
    if (!url || !isAudioLoaded || !duration) return;
    if (audioRef.current) {
      const newTime = Math.max(0, currentTime - 10);
      if (!isNaN(newTime)) {
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
      }
    }
  };

  // Calculate progress percentage safely
  const getProgressPercentage = () => {
    if (!duration || isNaN(duration) || !currentTime || isNaN(currentTime))
      return 0;
    const percentage = (currentTime / duration) * 100;
    return Math.min(100, Math.max(0, percentage));
  };

  return (
    <div className="space-y-2">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        key={`audio-${audioId}`}
        onLoadedData={() => setIsAudioLoaded(true)}
        onLoadedMetadata={() => {
          if (
            audioRef.current &&
            Number.isFinite(audioRef.current.duration) &&
            audioRef.current.duration > 0
          ) {
            setDuration(audioRef.current.duration);
          } else if (initialDuration && initialDuration > 0) {
            setDuration(initialDuration);
          }
          setIsAudioLoaded(true);
        }}
        onError={(e) => {
          console.error("Audio loading error:", e);
          setIsAudioLoaded(false);
          setDuration(initialDuration || 0);
        }}
      />

      <div
        className="h-2 bg-[#F5F6F7] rounded-full relative cursor-pointer"
        ref={progressBarRef}
        onClick={handleProgressClick}
        onMouseDown={handleProgressDragStart}
        onMouseUp={handleProgressDragEnd}
        onMouseLeave={handleProgressDragEnd}
        onMouseMove={handleProgressDrag}
      >
        <div
          className="h-full bg-gradient-to-r from-[#4400FF] to-[#9775F5] rounded-full"
          style={{ width: `${getProgressPercentage()}%` }}
        ></div>
      </div>

      <div className="flex justify-between items-center">
        <span className="text-xs text-[#474A52]">
          {formatTime(currentTime)}
        </span>
        <div className="flex items-center gap-4">
          <button
            onClick={skipBackward}
            className="p-2 transition-colors flex items-center justify-center hover:bg-[#F5F6F7]"
            title="Skip backward 10 seconds"
            disabled={!isAudioLoaded || !duration}
          >
            <img src={skipBackward10} />
          </button>

          <button
            onClick={handlePlayPause}
            className="w-12 h-12 flex items-center justify-center"
            disabled={!isAudioLoaded || !duration}
          >
            {isPlaying ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="12"
                  fill="url(#paint0_linear_pause)"
                />
                <rect x="8" y="8" width="3" height="8" rx="1" fill="white" />
                <rect x="13" y="8" width="3" height="8" rx="1" fill="white" />
                <defs>
                  <linearGradient
                    id="paint0_linear_pause"
                    x1="12"
                    y1="0"
                    x2="12"
                    y2="24"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#4400FF" />
                    <stop offset="1" stopColor="#9775F5" />
                  </linearGradient>
                </defs>
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="12"
                  fill="url(#paint0_linear_1777_124)"
                />
                <path
                  d="M18.9958 11.347C19.255 11.4967 19.255 11.8709 18.9958 12.0206L8.32033 18.1841C8.06107 18.3337 7.737 18.1466 7.737 17.8473L7.737 5.52033C7.737 5.22097 8.06107 5.03386 8.32033 5.18354L18.9958 11.347Z"
                  fill="white"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_1777_124"
                    x1="12"
                    y1="0"
                    x2="12"
                    y2="24"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#4400FF" />
                    <stop offset="1" stopColor="#9775F5" />
                  </linearGradient>
                </defs>
              </svg>
            )}
          </button>

          <button
            onClick={skipForward}
            className="p-2 transition-colors flex items-center justify-center hover:bg-[#F5F6F7]"
            title="Skip forward 10 seconds"
            disabled={!isAudioLoaded || !duration}
          >
            <img src={skipForward10} />
          </button>
        </div>
        <span className="text-xs text-[#474A52]">
          {formatTime(duration ?? 0)}
        </span>
      </div>
    </div>
  );
};

export default Recording;
